import { configure, defineRule } from 'vee-validate';
import { computed, reactive, ref, watch } from 'vue';
import { readUrlParams } from './returns-common.mjs';
import ReturnsWizardOrderFormComponent from './returns-step1-order-form.mjs';
import ReturnsWizardTypeSelectionComponent from './returns-step2-type-selection.mjs';
import ReturnsWizardItemSelectionComponent from './returns-step3-item-selection.mjs';
import ReturnsWizardLocationFormComponent from './returns-step4-location-form.mjs';
import ReturnsWizardThankYouComponent from './returns-step5-thank-you.mjs';

const nullFn = () => null;

/** @type {Record<import('./returns-api.js').TReturnType, string>} */
const returnTypeStrDictionary = {
	HOME: 'Reso da sito',
	STORE: 'Reso in negozio',
	SELFSERVICE: 'Reso autonomo',
}

/**
 * @typedef {object} Step
 * @property {string} id
 * @property {() => string | null} getPrevious
 * @property {(data: WizardData) => string} getTitle
 */

/** @type Record<string, Step> */
const STEPS = {
	ORDER_FORM: {
		id: 'order-form',
		getPrevious: nullFn,
		getTitle: () => `Step 1 di 4: Ricerca ordine`,
	},
	TYPE_SELECTION: {
		id: 'type-selection',
		getPrevious: () => STEPS.ORDER_FORM,
		getTitle: () => `Step 2 di 4: Seleziona la modalità di reso`
	},
	ITEM_SELECTION: {
		id: 'item-selection',
		getPrevious: () => STEPS.TYPE_SELECTION,
		getTitle: () => `Step 3 di 4: Seleziona gli articoli da restituire`
	},
	LOCATION_FORM: {
		id: 'location-form',
		getPrevious: () => STEPS.ITEM_SELECTION,
		getTitle: ( { returnType } ) => `Step 4 di 4: ${ returnTypeStrDictionary[ returnType ] }`
	},
	THANK_YOU: {
		id: 'thank-you',
		getPrevious: nullFn,
		getTitle: () => `Richiesta inviata`
	},
};

function setupVeeValidate() {
	const requiredValidator = ( value ) => {
		if ( value === null || value === undefined || value === false || value.trim().length === 0 ) {
			return 'Questo campo è obbligatorio.';
		}
		return true;
	};
	defineRule( 'required', requiredValidator );

	const emailValidator = ( value ) => {
		return String( value )
			.toLowerCase()
			.match(
				/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			) ? true : 'Inserisci un indirizzo email valido';
	};
	defineRule( 'email', emailValidator );

	const numberValidator = ( value ) => {
		return String( value )
			.trim()
			.match(
				/^[0-9]+$/
			) ? true : 'Inserisci un numero valido';
	};
	defineRule( 'number', numberValidator );

	configure( {} );
}

const ReturnsWizard = {
	setup() {

		setupVeeValidate();

		const wizardRef = ref( null );

		const { orderNumber, email, useStorage } = { orderNumber: '', email: '', useStorage: 'false', ...readUrlParams() };

		const stores = window.returnsConfiguration.settings.stores?.filter( v => v.pickup_enabled ) ?? [];
		const returnReasons = window.returnsConfiguration.settings.returnReasons ?? [];

		/**
		 * @typedef {object} WizardData
		 * @property {string | null} orderNumber
		 * @property {string | null} email
		 * @property {import('./returns-api.js').TGetOrderResult | null} order
		 * @property {import('./returns-api.js').TGetOrderResultSelectableItems} items
		 * @property {import('./returns-api.js').TReturnType | null} returnType
		 * @property {import('./returns-api.js').TCreateReturnResult | null} return
		 * @property {import('./returns-api.js').TReturnAddress} address
		 * @property {import('./returns-api.js').TReturnBankDeposit} bankDeposit
		 */

		/** @type WizardData */
		let wizardData = reactive( {

			// Step 1
			orderNumber: orderNumber,
			email: email,
			order: null,
			items: [],

			// Step 2
			type: null,

			// Step 4
			address: {
				name: '',
				surname: '',
				street: '',
				number: '',
				province: '',
				city: '',
				zip: '',
				phone: '',
				location: '',
			},
			store: {
				identifier: '',
				name: '',
				email: ''
			},
			bankDeposit: {
				iban: '',
				owner: '',
			},

			return: null

		} );

		/** @type {{ value: typeof STEPS[string]}} */
		const step = ref( STEPS.ORDER_FORM );

		/**** STORAGE ****/
		// On step change, save wizard data to sessionStorage.		
		watch( step, () => sessionStorage.setItem( 'returns', JSON.stringify( { wizardData, step: step.value.id } ) ) );

		if ( useStorage === 'true' ) {
			// Only try to restore session if parameter is true.
			const sessionData = JSON.parse( sessionStorage.getItem( 'returns' ) );
			if ( sessionData ) {
				wizardData = reactive( sessionData.wizardData );
				step.value = Object.values( STEPS ).find( step => step.id === sessionData.step );
			}
			console.warn( 'You are currently using experimental storage functionality.' );
		}
		/**** STORAGE ****/

		// On step change, scroll to top.		
		watch( step, () => wizardRef.value.scrollIntoView( true ) );

		/**
		 * @param data {object}
		 * @param data.orderNumber {string}
		 * @param data.email {string}
		 * @param data.order {import('./returns-api.js').TGetOrderResult}
		 */
		const onOrderAvailable = ( data ) => {
			wizardData.orderNumber = data.orderNumber;
			wizardData.email = data.email;
			wizardData.order = data.order;
			wizardData.items = data.order.returnableItems.map( item => ( { ...item, selected: false, reason: '' } ) ); // Reset items  on order change
			step.value = STEPS.TYPE_SELECTION;
		};

		/**
		 * @param type {import('./returns-api.js').TReturnType}
		 */
		const onTypeSelected = ( type ) => {
			wizardData.returnType = type;
			step.value = STEPS.ITEM_SELECTION;
		};

		/**
		 * @param type {import('./returns-api.js').TGetOrderResultSelectableItems}
		 */
		const onItemSelectionConfirmed = ( data ) => {
			wizardData.items = data;
			step.value = STEPS.LOCATION_FORM;
		};

		/**
		 * @param type {import('./returns-api.js').TCreateReturnResult}
		 */
		const onReturnCreated = ( data ) => {
			wizardData.return = data;
			step.value = STEPS.THANK_YOU;
		}

		const showBankDeposit = computed( () => wizardData.order.hasBankDeposit );

		const previousStep = computed( () => step.value?.getPrevious() );

		const stepTitle = computed( () => step.value?.getTitle( wizardData ) );

		const submitData = computed( () => {
			return {
				orderNumber: wizardData.orderNumber,
				email: wizardData.email,
				customerName: wizardData.order?.customerName,
				orderId: wizardData.order?.orderId,
				items: ( wizardData.items ?? [] ).filter( item => item.selected ).map( ( { id, name, sku, reason } ) => ( { id, name, sku, reason } ) ),
				returnType: wizardData.returnType,
				address: wizardData.returnType === 'HOME' ? wizardData.address : null,
				store: wizardData.returnType === 'STORE' ? wizardData.store : null,
				bankDeposit: wizardData.order?.hasBankDeposit ? wizardData.bankDeposit : null,
			};
		} );

		return {
			step,
			wizardRef,
			wizardData,
			onOrderAvailable,
			onTypeSelected,
			onItemSelectionConfirmed,
			onReturnCreated,
			showBankDeposit,
			previousStep,
			stepTitle,
			submitData,
			stores,
			returnReasons,
			STEPS
		}
	},
	template: `
		<div class="wizard-step max-w-[1180px] mx-auto flex flex-col items-stretch p-4" :class="[ 'wizard-step-' + step.id ]" ref="wizardRef">
			
			<div class="wizard-step-header">
				<a v-if="previousStep" @click="step = previousStep" class="cursor-pointer">← Torna allo step precedente</a>
				<div class="text-lg">{{ stepTitle }}</div>
			</div>

			<template v-if="step.id === STEPS.ORDER_FORM.id">
				<ReturnsWizardOrderFormComponent :email="wizardData.email" :orderNumber="wizardData.orderNumber" @orderAvailable="onOrderAvailable" />
			</template>

			<template v-else-if="step.id === STEPS.TYPE_SELECTION.id">
				<ReturnsWizardTypeSelectionComponent :selected="wizardData.returnType" @typeSelected="onTypeSelected" />
			</template>

			<template v-else-if="step.id === STEPS.ITEM_SELECTION.id">
				<ReturnsWizardItemSelectionComponent :items="wizardData.items" :returnReasons="returnReasons" @itemsConfirmed="onItemSelectionConfirmed" />
			</template>

			<template v-else-if="step.id === STEPS.LOCATION_FORM.id">
				<ReturnsWizardLocationFormComponent :returnType="wizardData.returnType" :showBankDeposit="showBankDeposit" v-model:address="wizardData.address" v-model:store="wizardData.store" v-model:bankDeposit="wizardData.bankDeposit" :stores="stores" :submitData="submitData" @returnCreated="onReturnCreated" />
			</template>

			<template v-else-if="step.id === STEPS.THANK_YOU.id">
				<ReturnsWizardThankYouComponent :return="wizardData.return" />
			</template>

			<template v-else>
				Unsupported step: {{ step }}
			</template>

		</div>
	`,
	components: {
		ReturnsWizardOrderFormComponent,
		ReturnsWizardTypeSelectionComponent,
		ReturnsWizardItemSelectionComponent,
		ReturnsWizardLocationFormComponent,
		ReturnsWizardThankYouComponent,
	}
};
export default ReturnsWizard;
